// ScanResult widget — display-only (H.3.4a).
//
// Renders the scan-invariants tool result: a `:::widget[scan-result]
// {payload="<base64 JSON>"}:::` directive. The payload is the projector's
// POST /admin/scan-invariants response. Decode → render a scanned/violations
// summary. No interaction (the confirmation already happened at the gate).

import { Card } from '@rmint/ui';

interface Props {
  payload?: string;
}

interface ScanData {
  scanned?: number;
  violations?: unknown[];
  ok?: boolean;
}

function decodePayload(payload?: string): ScanData | null {
  if (!payload) return null;
  try {
    return JSON.parse(atob(payload)) as ScanData;
  } catch {
    return null;
  }
}

export function ScanResult({ payload }: Props) {
  const data = decodePayload(payload);
  if (!data) {
    return (
      <div
        data-testid="scan-result"
        className="my-2 rounded border border-destructive/30 px-3 py-2 text-sm text-destructive"
      >
        Scan result: no payload.
      </div>
    );
  }

  const violations = data.violations ?? [];
  const clean = violations.length === 0;

  return (
    <Card data-testid="scan-result" className="my-2">
      <div className="text-sm font-semibold text-foreground mb-2">
        Invariant scan
      </div>
      <div className="flex gap-6 text-xs mb-2">
        {typeof data.scanned === 'number' ? (
          <div>
            <span className="text-lg font-bold text-foreground">{data.scanned}</span>
            <span className="text-foreground-mute ml-1">scanned</span>
          </div>
        ) : null}
        <div>
          <span
            className={`text-lg font-bold ${clean ? 'text-success' : 'text-danger'}`}
          >
            {violations.length}
          </span>
          <span className="text-foreground-mute ml-1">violations</span>
        </div>
      </div>
      {clean ? (
        <div className="text-xs text-success">All invariants hold.</div>
      ) : (
        <ul className="space-y-0.5 text-xs text-danger">
          {violations.map((v, i) => (
            <li key={i} className="font-mono">
              {typeof v === 'string' ? v : JSON.stringify(v)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
